import React, { useEffect, useState } from 'react';
import { useDecodedToken } from '../utils/useDecodedToken';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Button, IconButton
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SavingsIcon from '@mui/icons-material/Savings';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import { api } from '../api/client';
import FeatureGuard from '../components/common/FeatureGuard';
import UserTypeIndicator from '../components/common/UserTypeIndicator';
import { isAuthenticated } from '../utils/auth';

const COLORS = ['#1976d2', '#43a047', '#fbc02d', '#e53935', '#8e24aa', '#00bcd4'];

const Dashboard: React.FC = () => {
  const user = useDecodedToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<any>({});
  const [loans, setLoans] = useState<any[]>([]);
  const [activeLoans, setActiveLoans] = useState<any[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [repaymentTrend, setRepaymentTrend] = useState<any[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  // Cache configuration
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  const CACHE_KEY = 'dashboard_cache';
  const CACHE_TIMESTAMP_KEY = 'dashboard_cache_timestamp';

  // Cache utility functions
  const getCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cacheTimestamp) {
        const now = Date.now();
        const cacheTime = parseInt(cacheTimestamp);
        
        // Check if cache is still valid
        if (now - cacheTime < CACHE_DURATION) {
          const parsedData = JSON.parse(cachedData);
          // Validate cache belongs to current user
          const currentUserId = user?.sub || user?.preferred_username || user?.email || 'unknown';
          if (parsedData?.userId && parsedData.userId !== currentUserId) {
            clearCache();
            return null;
          }
          return parsedData;
        }
      }
    } catch (error) {
      console.error('[DASHBOARD CACHE] Error reading cache:', error);
      // Clear corrupted cache
      clearCache();
    }
    return null;
  };

  const setCachedData = (data: any) => {
    try {
      const dataWithUser = {
        ...data,
        userId: user?.sub || user?.preferred_username || user?.email || 'unknown'
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(dataWithUser));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('[DASHBOARD CACHE] Error caching data:', error);
    }
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('[DASHBOARD CACHE] Error clearing cache:', error);
    }
  };

  // Manual refresh function to force fresh data
  const refreshDashboard = () => {
    clearCache();
    setIsManualRefresh(true);
    setRefreshTrigger(prev => prev + 1); // Trigger useEffect to run again
  };

  // Note: Cache clearing on mount removed to allow caching to work


  useEffect(() => {
    const fetchDashboardData = async () => {
      // Check authentication before making any API calls
      if (!isAuthenticated()) {
        return;
      }

      // Check cache first (skip if manual refresh)
      const cachedData = getCachedData();
      if (cachedData && !isManualRefresh) {

        
        // Validate cached data has meaningful content
        const hasValidData = (cachedData.accounts && Object.keys(cachedData.accounts).length > 0) || 
                           (cachedData.loans && cachedData.loans.length > 0) || 
                           (cachedData.recentTransactions && cachedData.recentTransactions.length > 0);
        
        if (hasValidData) {

          setAccounts(cachedData.accounts || {});
          setLoans(cachedData.loans || []);
          setActiveLoans(cachedData.activeLoans || []);
          setRecentTransactions(cachedData.recentTransactions || []);
          setRepaymentTrend(cachedData.repaymentTrend || []);
          setLoading(false);
          return;
        } else {

        }
      }

      setError('');
      try {
        // Validate accountNumber strictly: must be a non-empty, non-literal id
        const rawAcc = (user?.accountNumber ?? '') as string;
        const accountNumberValid = !!rawAcc && rawAcc.trim() !== '' && rawAcc.trim().toLowerCase() !== 'undefined' && rawAcc.trim().toLowerCase() !== 'null';

        // For users with valid account numbers, fetch full account data
        if (accountNumberValid) {
          try {
            const accRes = await api.get(`/api/clients/${rawAcc}/accounts`);

            setAccounts(accRes.data);
            const loanAccounts = accRes.data.loanAccounts || [];

            
            // Fetch detailed loan data using associations endpoint for each loan
            const detailedLoans = [];
            for (const loanAccount of loanAccounts) {
              try {

                const detailedLoanRes = await api.get(`/api/loans/${loanAccount.id}/associations?associations=repaymentSchedule`);

                detailedLoans.push(detailedLoanRes.data);
              } catch (loanErr: any) {
                console.error(`Failed to fetch detailed data for loan ${loanAccount.id}:`, loanErr);
                // If detailed fetch fails, use the basic loan account data
                detailedLoans.push(loanAccount);
              }
            }
            

            setLoans(detailedLoans);
            setActiveLoans(detailedLoans.filter((loan: any) => (loan.status?.value || '').toLowerCase() === 'active'));
            
          } catch (accErr: any) {
            console.error('Failed to fetch accounts:', accErr);
            setAccounts({ loanAccounts: [], savingsAccounts: [] });
            setLoans([]);
            setActiveLoans([]);
          }
          
          // Fetch recent transactions using loan associations endpoint
          try {
            // Get client accounts to find loan IDs
            const accountsRes = await api.get(`/api/clients/${rawAcc}/accounts`);
            const loanAccounts = accountsRes.data.loanAccounts || [];

            
            // Fetch transactions for each loan
            const allTransactions: any[] = [];
            for (const loan of loanAccounts) {
              try {
                const loanTransactionsRes = await api.get(`/api/loans/${loan.id}/associations?associations=transactions`);

                
                // Check for transactions in different possible locations
                let loanTransactions = loanTransactionsRes.data.transactions || [];
                
                // If not found in data.transactions, check if the entire response is the transactions array
                if (!Array.isArray(loanTransactions) || loanTransactions.length === 0) {
                  if (Array.isArray(loanTransactionsRes.data)) {
                    loanTransactions = loanTransactionsRes.data;
                  }
                }
                

                
                if (Array.isArray(loanTransactions) && loanTransactions.length > 0) {
                  allTransactions.push(...loanTransactions);
                }
              } catch (loanErr: any) {
                console.error(`Failed to fetch transactions for loan ${loan.id}:`, loanErr);
              }
            }
            

            setRecentTransactions(allTransactions);
            
            // Calculate real repayment trends from transactions
            if (allTransactions.length > 0) {

              
              const monthlyRepayments = new Map<string, number>();
              
              // Initialize last 6 months
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const currentDate = new Date();
              for (let i = 5; i >= 0; i--) {
                const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                const monthKey = months[monthDate.getMonth()];
                monthlyRepayments.set(monthKey, 0);
              }
              
              
              // Process transactions to find repayments
              let repaymentCount = 0;
              allTransactions.forEach((tx: any) => {
                if (tx.date && Array.isArray(tx.date) && tx.date.length === 3) {
                  const txDate = new Date(tx.date[0], tx.date[1] - 1, tx.date[2]);
                  const monthKey = months[txDate.getMonth()];
                  
                  // Check if this is a repayment transaction
                  const txType = (tx.type?.value || '').toLowerCase();
                  const txNarration = (tx.narration || '').toLowerCase();
                  const txDescription = (tx.description || '').toLowerCase();
                  
                  const isRepayment = txType.includes('repayment') || 
                                    txType.includes('payment') ||
                                    txType.includes('repay') ||
                                    txNarration.includes('repayment') ||
                                    txNarration.includes('payment') ||
                                    txNarration.includes('repay') ||
                                    txDescription.includes('repayment') ||
                                    txDescription.includes('payment') ||
                                    txDescription.includes('repay') ||
                                    (tx.amount && tx.amount < 0) || // Negative amount usually indicates payment
                                    txNarration.includes('l/rep') || // Loan repayment abbreviation
                                    txNarration.includes('loan repay'); // Loan repayment
                  
                  if (isRepayment && monthlyRepayments.has(monthKey)) {
                    const currentAmount = monthlyRepayments.get(monthKey) || 0;
                    const repaymentAmount = Math.abs(tx.amount || 0);
                    monthlyRepayments.set(monthKey, currentAmount + repaymentAmount);
                    repaymentCount++;

                  }
                }
              });
              

              
              // Convert to array format for the chart
              const repaymentTrendData = Array.from(monthlyRepayments.entries()).map(([month, amount]) => ({
                month,
                repaid: Math.round(amount)
              }));
              

              setRepaymentTrend(repaymentTrendData);
            } else {

              // Fallback to empty data if no transactions
              setRepaymentTrend([
                { month: 'Jan', repaid: 0 },
                { month: 'Feb', repaid: 0 },
                { month: 'Mar', repaid: 0 },
                { month: 'Apr', repaid: 0 },
                { month: 'May', repaid: 0 },
                { month: 'Jun', repaid: 0 },
              ]);
            }
          } catch (txErr: any) {
            console.error('Failed to fetch transactions:', txErr);
            setRecentTransactions([]);
            // Set empty repayment trend on error
            setRepaymentTrend([
              { month: 'Jan', repaid: 0 },
              { month: 'Feb', repaid: 0 },
              { month: 'Mar', repaid: 0 },
              { month: 'Apr', repaid: 0 },
              { month: 'May', repaid: 0 },
              { month: 'Jun', repaid: 0 },
            ]);
          }
        } else {
          // For users without account numbers (loan applicants, new users), set empty defaults



          setAccounts({ loanAccounts: [], savingsAccounts: [] });
          setLoans([]);
          setActiveLoans([]);
          setRecentTransactions([]);
          // Set empty repayment trend for users without accounts
          setRepaymentTrend([
            { month: 'Jan', repaid: 0 },
            { month: 'Feb', repaid: 0 },
            { month: 'Mar', repaid: 0 },
            { month: 'Apr', repaid: 0 },
            { month: 'May', repaid: 0 },
            { month: 'Jun', repaid: 0 },
          ]);
        }
        
      } catch (err: any) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Unable to load dashboard data. The external service may be temporarily unavailable.');
        // Set empty defaults to prevent crashes
        setAccounts({ loanAccounts: [], savingsAccounts: [] });
        setLoans([]);
        setActiveLoans([]);
        setRecentTransactions([]);
      } finally {
        setIsManualRefresh(false); // Reset manual refresh flag
      }
    };

    // Save data to cache after successful fetch
    const saveToCache = () => {
      const dataToCache = {
        accounts,
        loans,
        activeLoans,
        recentTransactions,
        repaymentTrend
      };
      setCachedData(dataToCache);
    };

    fetchDashboardData();
  }, [user?.sub, user?.accountNumber, refreshTrigger, isManualRefresh]); // Added dependencies

  // Save data to cache when it changes (but not on initial load)
  useEffect(() => {
    // Only save to cache if we have meaningful data and we're not loading
    const hasData = (accounts && Object.keys(accounts).length > 0) || 
                   (loans && loans.length > 0) || 
                   (recentTransactions && recentTransactions.length > 0);
    
    if (!loading && hasData && !isManualRefresh) {

      const dataToCache = {
        accounts,
        loans,
        activeLoans,
        recentTransactions,
        repaymentTrend
      };
      setCachedData(dataToCache);
    }
  }, [accounts, loans, activeLoans, recentTransactions, repaymentTrend, loading, isManualRefresh]);

  // Calculate totals by currency from loan accounts
  // Use the loans state which is properly set from the API response
  const loanAccounts = loans || [];
  const savingsAccounts = accounts.savingsAccounts || [];
  



  
  // Loan-related calculations from loan accounts
  const loanCountByCurrency: Record<string, number> = {};
  const loanTotalsByCurrency: Record<string, number> = {};
  const outstandingTotalsByCurrency: Record<string, number> = {};
  const disbursedTotalsByCurrency: Record<string, number> = {};
  const awaitingDisbursalTotalsByCurrency: Record<string, number> = {};
  
  loanAccounts.forEach((loan: any) => {

    const currency = loan.currency?.code;
    if (currency) {
      // Count loans by currency
      loanCountByCurrency[currency] = (loanCountByCurrency[currency] || 0) + 1;
      
      // Total loan amount (only for actually disbursed loans)
      let disbursedAmount = loan.disbursedAmount || loan.summary?.principalDisbursed || loan.originalLoan || loan.principal || 0;
      
      // Check if loan has been actually disbursed by looking for actualDisbursementDate
      const hasBeenDisbursed = loan.timeline?.actualDisbursementDate && 
        Array.isArray(loan.timeline.actualDisbursementDate) && 
        loan.timeline.actualDisbursementDate.length > 0;
      
      if (hasBeenDisbursed && disbursedAmount > 0) {
        // Use Skyfin amounts as-is (no USD→ZIG conversion)
        // Total disbursed across all loans (historical disbursed)
        loanTotalsByCurrency[currency] = (loanTotalsByCurrency[currency] || 0) + disbursedAmount;

        // Only count disbursed amount for ACTIVE loans in the "Active Loans Disbursed" card
        const isActive = (loan.status?.active === true)
          || ((loan.status?.value || '').toLowerCase() === 'active')
          || ((loan.status?.code || '').toLowerCase().includes('active'));
        if (isActive) {
          disbursedTotalsByCurrency[currency] = (disbursedTotalsByCurrency[currency] || 0) + disbursedAmount;
        }

      } else {
        // Check if loan is approved and waiting for disbursal
        const isApproved = (loan.status?.value || '').toLowerCase() === 'approved';
        const isWaitingForDisbursal = loan.status?.waitingForDisbursal === true;
        
        if (isApproved && isWaitingForDisbursal && disbursedAmount > 0) {
          awaitingDisbursalTotalsByCurrency[currency] = (awaitingDisbursalTotalsByCurrency[currency] || 0) + disbursedAmount;

        } else {
        }
      }
      
      // Outstanding balance (prefer totalOutstanding per Fineract summary, then fallback)
      // Use Skyfin amounts as-is (no USD→ZIG conversion)
      let outstandingAmount = (loan.summary?.totalOutstanding ?? loan.outstandingBalance ?? loan.summary?.principalOutstanding ?? 0);
      outstandingTotalsByCurrency[currency] = (outstandingTotalsByCurrency[currency] || 0) + outstandingAmount;
      

    }
  });
  
  
  
  // Savings calculations
  const savingsTotalsByCurrency: Record<string, number> = {};
  savingsAccounts.forEach((savings: any) => {
    const currency = savings.currency?.code;
    if (currency) {
      savingsTotalsByCurrency[currency] = (savingsTotalsByCurrency[currency] || 0) + (savings.balance || 0);
    }
  });
  
  const totalNumberOfLoans = loanAccounts.length;

  // Prepare loan status breakdown for pie chart
  const statusCount: Record<string, number> = {};
  loanAccounts.forEach((loan: any) => {
    const status = (loan.status?.value || 'unknown').toLowerCase();
    statusCount[status] = (statusCount[status] || 0) + 1;
  });
  const loanStatusData = Object.entries(statusCount).map(([status, value], i) => ({ name: status.charAt(0).toUpperCase() + status.slice(1), value }));

  // Collect all currencies present in loan and savings accounts, always include USD and ZiG
  const allCurrencies = Array.from(new Set([
    ...loanAccounts.map((acc: any) => acc.currency?.code).filter(Boolean),
    ...savingsAccounts.map((acc: any) => acc.currency?.code).filter(Boolean),
    'USD', 'ZiG'
  ]));

  // If not authenticated, show loading or redirect
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, letterSpacing: 1, mb: 0 }}>
          Welcome, {user?.preferred_username || 'User'}!
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={refreshDashboard}
            disabled={loading}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.50'
              }
            }}
            title="Refresh Dashboard Data"
          >
            <RefreshIcon />
          </IconButton>
          <UserTypeIndicator />
        </Box>
      </Box>

      {/* Single indicator: show alert description only when not full access */}
      <FeatureGuard feature="accounts" showAlert>{null}</FeatureGuard>
      {!user?.accountNumber || String(user.accountNumber).toLowerCase() === 'undefined' || String(user.accountNumber).toLowerCase() === 'null' ? (
        <UserTypeIndicator variant="alert" showDescription />
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => window.location.reload()}
              sx={{ mr: 1 }}
            >
              Retry
            </Button>
            <Typography variant="body2" color="text.secondary">
              This may be due to temporary connectivity issues with the external service.
            </Typography>
          </Box>
        </Alert>
      ) : (
        <>
          {/* Show different content based on user type */}
          {!user?.accountNumber ? (
            <>
              {/* Content for users without account numbers (loan applicants, new users) */}
              <Box sx={{ mb: 3 }}>
                <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Welcome to SMEDCO Loan Services!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You currently have <strong>limited access</strong> to account features. To unlock full access, please verify your SMEDCO account number.
                    </Typography>
                    {user?.trackingNumber ? (
                      <>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          You have a loan application in progress with tracking number: <strong>{user.trackingNumber}</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => navigate('/loan-application')}
                            sx={{ mr: 2 }}
                          >
                            Track Application
                          </Button>
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={() => navigate('/loan-application')}
                          >
                            Create New Application
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          You can create a new loan application or track existing ones. This feature is available to all users.
                        </Typography>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          onClick={() => navigate('/loan-application')}
                          sx={{ mb: 2 }}
                        >
                          Start Loan Application
                        </Button>
                      </>
                    )}
                    
                    {/* Access Information */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        <strong>What you can do now:</strong>
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        <li>✅ Create and manage loan applications</li>
                        <li>✅ Track application progress</li>
                        <li>✅ Make payments for applications</li>
                        <li>✅ View application status</li>
                      </Box>
                      
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                        <strong>To unlock full access:</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Provide your SMEDCO account number during registration or contact support to verify your existing account.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Loan Application Status for Loan Applicants */}
              {user?.trackingNumber && (
                <Box sx={{ mb: 3 }}>
                  <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="secondary">
                        Loan Application Status
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Your loan application is currently being processed. You can track the progress and make payments for your application.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button 
                          variant="contained" 
                          color="secondary" 
                          onClick={() => navigate('/payments')}
                        >
                          Make Payment
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="secondary" 
                          onClick={() => navigate('/loan-application')}
                        >
                          View Application Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
              
              {/* New User Information */}
              {!user?.trackingNumber && !user?.accountNumber && (
                <Box sx={{ mb: 3 }}>
                  <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="info">
                        Getting Started
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Welcome to SMEDCO! You can start by creating your first loan application. Our team will review your application and guide you through the process.
                      </Typography>
                      <Button 
                        variant="contained" 
                        color="info" 
                        onClick={() => navigate('/loan-application')}
                      >
                        Create Loan Application
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          ) : (
            // Full dashboard for users with account numbers
            <>
              {/* Statistics Cards */}
              <FeatureGuard feature="accounts" showAlert={false}>
              <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Total Number of Loans</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                          {loanCountByCurrency[currency] || 0}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Total Disbursed Amount</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                          {(loanTotalsByCurrency[currency] || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Active Loans Disbursed</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                          {(disbursedTotalsByCurrency[currency] || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotificationsIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Loans Awaiting Disbursal</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="info.main" sx={{ fontWeight: 600 }}>
                          {(awaitingDisbursalTotalsByCurrency[currency] || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Total Outstanding</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="warning.main" sx={{ fontWeight: 600 }}>
                          {(outstandingTotalsByCurrency[currency] || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SavingsIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Total Savings</Typography>
                  </Box>
                  <Box>
                    {allCurrencies.map((currency) => (
                      <Box key={currency} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{currency}:</Typography>
                        <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                          {(savingsTotalsByCurrency[currency] || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          </FeatureGuard>

          {/* Charts Row */}
          <FeatureGuard feature="accounts" showAlert={false}>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc', height: 340 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PieChartOutlineIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Loan Status Breakdown</Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={loanStatusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#1976d2"
                        label
                      >
                        {loanStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc', height: 340 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Repayment Trend</Typography>
                  </Box>
                  {repaymentTrend.some(item => item.repaid > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={repaymentTrend}>
                        <XAxis dataKey="month" />
                        <YAxis 
                          tickFormatter={(value) => `$${value.toLocaleString()}`}
                        />
                        <Tooltip 
                          formatter={(value: any) => [`$${value.toLocaleString()}`, 'Repaid Amount']}
                          labelFormatter={(label) => `${label} 2025`}
                        />
                        <Legend />
                        <Bar 
                          dataKey="repaid" 
                          fill="#43a047" 
                          name="Repaid Amount"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: 250,
                      color: 'text.secondary'
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                      <Typography variant="body1" align="center">
                        No repayment data available
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        Repayment trends will appear here once payments are made
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          </FeatureGuard>

          {/* Recent Transactions Table */}
          <FeatureGuard feature="transactions" showAlert={false}>
          <Card elevation={4} sx={{ borderRadius: 3, background: '#f8fafc', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Recent Transactions</Typography>
              </Box>
              {(() => {
                const filteredRecentTransactions = (Array.isArray(recentTransactions) ? recentTransactions : []).filter((tx: any) => {
                  const t = `${tx.type?.value || ''} ${tx.narration || ''} ${tx.description || ''}`.toLowerCase();
                  return !t.includes('accrual');
                });
                if (filteredRecentTransactions.length === 0) {
                  return <Typography>No recent transactions found.</Typography>;
                }
                return (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableBody>
                      <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Date</b></TableCell>
                        <TableCell><b>Type</b></TableCell>
                        <TableCell><b>Amount</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                        <TableCell><b>Description</b></TableCell>
                      </TableRow>
                      {filteredRecentTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                          <TableCell>{tx.amount?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={tx.manuallyReversed ? 'Reversed' : 'Active'} 
                              color={tx.manuallyReversed ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                );
              })()}
            </CardContent>
          </Card>
          </FeatureGuard>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard; 



